import { useState, useEffect } from "react";
import styles from "./UpcomingLiveSession.module.css";
import { FiSearch } from "react-icons/fi";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import SessionCard from "../../components/LiveSessionCard/SessionCard";
import useDashboardStore from "../../store/dashboardStore";
import CustomDatePicker from "../Calendar/CustomDatePicker";
import InstructorSelector from "../InstructorSelector/InstructorSelector";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { FaRegCalendarPlus, FaRegCalendarCheck } from "react-icons/fa";
import { toast } from "react-toastify";
import { downloadSessionsIcs } from "../../utils/calendarExport";

dayjs.extend(utc);

const UpcomingLiveSessions = () => {
  const { communityIndex, communities, traders } = useDashboardStore();
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState("");
  const [date, setDate] = useState(null);
  const [selectedInstructorId, setSelectedInstructorId] = useState(null);

  const handleDateChange = (date) => setDate(date);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const selectedCommunityId = communities[communityIndex].id;
        const { data } = await api.get(
          ENDPOINTS.GET_LIVE_SESSION_BY_COMMUNITY_ID(selectedCommunityId),
        );
        if (data.success) {
          setSessions(data.sessions);
        }
      } catch (err) {
        console.error("Failed to fetch sessions", err);
      }
    };

    fetchSessions();
  }, [communities, communityIndex]);

  function isSameUTCDate(date1, date2) {
    if (!date1 || !date2) return false;

    const d1 = dayjs(date1);
    const d2 = dayjs(date2);
    return d1.isSame(d2, "day");
  }

  // Flip added_to_calendar locally after a successful add (single or bulk).
  const markSessionsAdded = (ids) => {
    setSessions((prev) =>
      prev.map((s) =>
        ids.includes(s.id) ? { ...s, added_to_calendar: true } : s,
      ),
    );
  };

  const filteredSessions = sessions.filter((session) => {
    const titleMatch = session.session_title
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const instructorMatch =
      selectedInstructorId == null ||
      session.instructor_id === selectedInstructorId;

    const dateMatch =
      !date ||
      !session.start_datetime ||
      isSameUTCDate(date, session.start_datetime);

    return titleMatch && instructorMatch && dateMatch;
  });

  // Bulk add: only sessions currently visible (day/search/instructor filters)
  // that aren't already added. The single .ics file works for Apple Calendar
  // and Outlook directly; Google Calendar users import it once.
  const pendingCalendarSessions = filteredSessions.filter(
    (s) => !s.added_to_calendar,
  );

  const bulkAddToCalendar = async (sessionsToAdd) => {
    downloadSessionsIcs(sessionsToAdd, "eau-sessions.ics");
    try {
      await api.post(ENDPOINTS.MARK_SESSIONS_CALENDAR, {
        session_ids: sessionsToAdd.map((s) => s.id),
      });
    } catch (err) {
      console.error("Failed to save calendar marks", err);
    }
    markSessionsAdded(sessionsToAdd.map((s) => s.id));
    toast.success(
      `Calendar file with ${sessionsToAdd.length} session${
        sessionsToAdd.length === 1 ? "" : "s"
      } downloaded — open it to add them (Google Calendar: import it at calendar.google.com)`,
    );
  };

  const handleAddAll = () => {
    if (pendingCalendarSessions.length > 0) {
      bulkAddToCalendar(pendingCalendarSessions);
      return;
    }
    if (
      filteredSessions.length > 0 &&
      window.confirm(
        `All ${filteredSessions.length} visible sessions are already in your calendar. Add them all again? This will create duplicate events.`,
      )
    ) {
      bulkAddToCalendar(filteredSessions);
    }
  };

  const selectedDaySessions = sessions.filter((session) => {
    const dateMatch =
      !date ||
      !session.start_datetime ||
      isSameUTCDate(date, session.start_datetime);

    return dateMatch;
  });

  const filteredTraders = traders
    .filter((trader) =>
      selectedDaySessions.some(
        (session) => session.instructor_id === trader.id,
      ),
    )
    .map((e) => ({
      id: e.id,
      name: e.display_name,
      profile_pic: e.profile_pic,
    }));

  return (
    <div className={styles.container}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>CALENDAR - LIVE SESSIONS</h2>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Type in to search .."
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FiSearch className={styles.searchIcon} />
        </div>
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.leftPanel}>
          <CustomDatePicker
            preSelectedDate={date}
            onDateSelect={handleDateChange}
          />
          {filteredTraders.length > 0 && (
            <InstructorSelector
              instructors={filteredTraders}
              selectedId={selectedInstructorId}
              onSelect={(id) => {
                if (id === selectedInstructorId) {
                  setSelectedInstructorId(null);
                } else {
                  setSelectedInstructorId(id);
                }
              }}
            />
          )}
        </div>
        <div className={styles.rightPanel}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <h2 className={styles.heading}>Selected Date Sessions</h2>
            {filteredSessions.length > 0 && (
              <button
                type="button"
                onClick={handleAddAll}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  border: "1px solid #158CA5",
                  background: "none",
                  color: pendingCalendarSessions.length > 0 ? "#158CA5" : "#2e9e5b",
                  borderColor:
                    pendingCalendarSessions.length > 0 ? "#158CA5" : "#2e9e5b",
                  borderRadius: 18,
                  padding: "6px 14px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {pendingCalendarSessions.length > 0 ? (
                  <>
                    <FaRegCalendarPlus />
                    Add all ({pendingCalendarSessions.length})
                  </>
                ) : (
                  <>
                    <FaRegCalendarCheck />
                    All added
                  </>
                )}
              </button>
            )}
          </div>
          {filteredSessions.map((session, i) => (
            <SessionCard
              key={session.id ?? i}
              session={session}
              onCalendarMarked={markSessionsAdded}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default UpcomingLiveSessions;
